import React from "react";
import Select from "react-select";
import { cn } from "../../../utils/cn";
import type {
  AgendaConsultant,
  AgendaFiltersState,
} from "../agendaTypes";

type AgendaFiltersProps = {
  filters: AgendaFiltersState;
  cityOptions: string[];
  consultants: AgendaConsultant[];
  showCityFilter: boolean;
  showConsultantFilter: boolean;
  onChange: (patch: Partial<AgendaFiltersState>) => void;
  className?: string;
  layout?: "default" | "toolbar";
  cityAsSelect?: boolean;
  dense?: boolean;
};

const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 30,
    height: 30,
    fontSize: 12,
    borderColor: "#E2E8F0",
    boxShadow: "none",
    borderRadius: 8,
    cursor: "pointer",
  }),
  valueContainer: (base: Record<string, unknown>) => ({
    ...base,
    padding: "0 6px",
    cursor: "pointer",
  }),
  indicatorsContainer: (base: Record<string, unknown>) => ({
    ...base,
    height: 30,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    fontSize: 12,
    cursor: "pointer",
  }),
  option: (base: Record<string, unknown>) => ({
    ...base,
    cursor: "pointer",
  }),
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 100,
  }),
};

const AgendaFilters: React.FC<AgendaFiltersProps> = ({
  filters,
  cityOptions,
  consultants,
  showCityFilter,
  showConsultantFilter,
  onChange,
  className,
  layout = "default",
  cityAsSelect = false,
  dense = false,
}) => {
  const consultantSelectOptions = [
    { value: 0, label: "Consultor: todos" },
    ...consultants.map((consultant) => ({
      value: consultant.id,
      label: consultant.first_name,
    })),
  ];

  const cityChips = showCityFilter ? (
    <div
      className={cn(
        "min-w-0",
        layout === "toolbar"
          ? "max-w-full min-w-0 shrink overflow-x-auto px-1.5 py-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden xl:max-w-[min(42vw,520px)]"
          : "max-w-full overflow-x-auto px-1.5 py-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2",
          layout === "toolbar" ? "flex-nowrap" : "flex-wrap"
        )}
      >
        <FilterChip
          active={!filters.city}
          label="Todas"
          onClick={() => onChange({ city: undefined })}
        />
        {cityOptions.map((city) => (
          <FilterChip
            key={city}
            active={filters.city === city}
            label={city}
            onClick={() => onChange({ city })}
          />
        ))}
      </div>
    </div>
  ) : null;

  const citySelect = showCityFilter && cityAsSelect ? (
    <div className={cn(dense ? "w-[126px]" : "w-[150px]", "shrink-0")}>
      <Select
        options={[
          { value: "", label: "Todas" },
          ...cityOptions.map((city) => ({ value: city, label: city })),
        ]}
        value={[
          { value: "", label: "Todas" },
          ...cityOptions.map((city) => ({ value: city, label: city })),
        ].find((option) => option.value === (filters.city ?? ""))}
        onChange={(option) =>
          onChange({ city: option?.value ? String(option.value) : undefined })
        }
        isSearchable={false}
        classNamePrefix="agenda-filter-select"
        styles={selectStyles}
        menuPlacement="top"
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      />
    </div>
  ) : null;

  const consultantControl = showConsultantFilter ? (
    <div className={cn(dense ? "w-[170px]" : "w-[188px]", "shrink-0")}>
      <Select
        options={consultantSelectOptions}
        value={
          consultantSelectOptions.find(
            (option) => option.value === (filters.consultantId ?? 0)
          ) ?? consultantSelectOptions[0]
        }
        onChange={(option) =>
          onChange({
            consultantId:
              option && option.value > 0 ? option.value : undefined,
          })
        }
        classNamePrefix="agenda-filter-select"
        styles={selectStyles}
        menuPlacement="top"
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      />
    </div>
  ) : null;

  if (layout === "toolbar") {
    return (
      <section
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end xl:max-w-[calc(100%-400px)] xl:flex-nowrap",
          className
        )}
      >
        {cityAsSelect ? citySelect : cityChips}
        <div className="flex shrink-0 items-center gap-1.5">
          {consultantControl}
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-2 xl:justify-end",
        className
      )}
    >
      {cityAsSelect ? citySelect : cityChips}
      {consultantControl}
    </section>
  );
};

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "shrink-0 rounded-full border px-2.5 py-1.5 text-[11px] font-semibold transition",
        active
          ? "border-[#c4552b]/40 bg-[#FFF5F0] text-[#c4552b]"
          : "border-[#E2E8F0] bg-white text-[#4A5568] hover:border-[#CBD5E0] hover:bg-[#FAFAFA]"
      )}
    >
      {label}
    </button>
  );
}

export default AgendaFilters;
